// Single shared Socket.IO client for the dashboard realtime feed (§7).
//
// FAITHFUL re-platform of the Backbone `universal_map.js` `fn['create.websocket']`
// connection, which builds ONE `io(`${location.hostname}/${serverName}`)` client,
// stores it on `window.ksdm_socket`, and (on reconnect) expands transports from
// `['websocket']` to `['polling','websocket']`. The trailing `/serverName` is a
// Socket.IO NAMESPACE keyed on the deploy server name (Django-injected); React
// reads it from `window.__KSDM_CONFIG__.serverName`.
//
// Differences from the legacy:
//  - We register the `'data'` listener EXACTLY ONCE (via `useProjectSocket`). The
//    Backbone `onSocketData` helper admits it leaks listeners ("Awful function …
//    REFACTOR this!") because every caller re-binds `socket.on('data', …)`.
//  - Connection MUST fail gracefully when no socket server is reachable (dev3 has
//    none): socket.io retries in the background; we surface no uncaught errors and
//    never block the page. We disable the throwing `connect_error` default by
//    attaching a no-op handler.

import { io, Socket } from 'socket.io-client';
import { getKsdmConfig } from 'helpers/index';

/**
 * Inbound `'data'` payload (syncdata), §7. The Node Socket.IO server emits this
 * after consuming the AMQP `syncdata` queue published by Django post_save /
 * post_delete signals.
 */
export interface SocketDataMessage {
  status?: string;
  app?: string;
  code?: 'post' | 'put' | 'delete';
  model?: string; // Django model name, e.g. 'ksdmproject'
  id?: number;
  data?: { id: number; [key: string]: unknown };
  permissions?: { project_id?: number };
  type?: string; // 'syncdata'
  /** Present when the message is a project-notification update (§7). */
  update_project_notification_data?: boolean | unknown;
  [key: string]: unknown;
}

/**
 * Resolve the socket URL/namespace exactly like the Backbone app:
 * `${location.hostname}/${serverName}`. serverName falls back to a
 * subdomain-derived name then `'local'` (matching the legacy cfg fallback)
 * so a missing config never produces an empty namespace.
 */
function resolveSocketUrl(): string {
  const cfg = getKsdmConfig();
  let serverName = cfg?.serverName;
  if (!serverName) {
    const parts = location.hostname.split('.');
    serverName =
      parts.length === 2 || parts[0] === 'www' ? 'dev' : parts[0] || 'local';
  }
  return `${location.hostname}/${serverName}`;
}

let socket: Socket | null = null;

/**
 * Lazily create (once) and return the shared Socket.IO client. Subsequent calls
 * return the SAME instance — the connection is created exactly once for the app,
 * matching `window.ksdm_socket`.
 */
export function getSocket(): Socket {
  if (socket) return socket;

  const url = resolveSocketUrl();
  // socket.io-client v4: prefer websocket, fall back to polling on reconnect
  // (legacy parity). `reconnection` is on by default; retries happen in the
  // background and are harmless when no server is reachable.
  socket = io(url, {
    transports: ['websocket'],
    // Keep retrying quietly; bound the backoff so we don't hammer a dead host.
    reconnection: true,
    reconnectionDelayMax: 10000,
    // Do not auto-emit auth; the legacy server uses no app-level auth.
  });

  // On reconnection, expand transports (the WebSocket upgrade may have failed
  // behind a proxy/firewall) — verbatim from universal_map.js.
  socket.io.on('reconnect_attempt', () => {
    socket!.io.opts.transports = ['polling', 'websocket'];
  });

  // GRACEFUL FAILURE: swallow connection errors so they never surface as
  // uncaught rejections or break the page (no socket server in local Vite dev;
  // NOTE dev3 DOES have one — nginx routes /socket.io to the python-socketio
  // server, namespace /<serverName>, verified connected 2026-07-07).
  socket.on('connect_error', () => {
    /* no-op: connection unavailable (dev3) — retry silently */
  });
  socket.io.on('error', () => {
    /* no-op: low-level transport error — retry silently */
  });

  // Expose for parity with the Backbone `window.ksdm_socket` (debug / interop).
  (window as unknown as { ksdm_socket?: Socket }).ksdm_socket = socket;

  return socket;
}

/** Disconnect and clear the shared client (used on full app teardown / tests). */
export function disconnectSocket(): void {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
    delete (window as unknown as { ksdm_socket?: Socket }).ksdm_socket;
  }
}
